import React, { useEffect, useState } from 'react';
import Colors from '../../src/themes/Colors';
import { Progress, Typography, Button } from 'antd';
import { getDomain } from '../services/utils';

import logoLatinad from '../images/planner.png';
import logoFenix from '../images/whiteLabel/fenix-publinet.png';
import logoGrupovia from '../images/whiteLabel/grupo-via.svg';
import logoMediaShake from '../images/whiteLabel/media-shake.png';
import logoSmartFit from '../images/whiteLabel/smart-fit.png';
import logoDuoPrint from '../images/whiteLabel/duo-print.png';
import logoTerritorioDigital from '../images/whiteLabel/territorio-digital.png';
import logoMovImagen from '../images/whiteLabel/movimagen.png';
import logoYoda from '../images/whiteLabel/yoda.png';
import logoHakMedia from '../images/whiteLabel/hak-media.png';
import logoBlaster from '../images/whiteLabel/blaster.png';
import logoLaMediatica from '../images/whiteLabel/laMediatica.png';
import logoDv360 from '../images/whiteLabel/dv360.png';

const { Text, Paragraph } = Typography;

const domain = getDomain(window.location.hostname);
const isLatinAd = domain === 'latinad';

const whiteLabelLogos = {
  latinad: logoLatinad,
  fenix: logoFenix,
  grupoVia: logoGrupovia,
  mediaShake: logoMediaShake,
  smartFit: logoSmartFit,
  duoPrint: logoDuoPrint,
  territorioDigital: logoTerritorioDigital,
  movImagen: logoMovImagen,
  yoda: logoYoda,
  hakMedia: logoHakMedia,
  blaster: logoBlaster,
  laMediatica: logoLaMediatica,
  dv360: logoDv360
};

const whiteLabelLogo = whiteLabelLogos[domain] || logoLatinad;

export default function AILoader({ onAnimationComplete, fullText, t }) {
  const [progress, setProgress] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showStartButton, setShowStartButton] = useState(false);

  const [floatingParticles] = useState(() =>
    [...Array(25)].map((_, i) => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      animationDelay: `${i * 0.5}s`
    }))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          if (!isTyping) {
            setIsTyping(true);
          }
          return 100;
        }
        return prev + 2;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isTyping]);

  useEffect(() => {
    if (isTyping && typedText.length < fullText.length) {
      const timeout = setTimeout(() => {
        setTypedText(fullText.slice(0, typedText.length + 1));
      }, 25);

      return () => clearTimeout(timeout);
    }
  }, [isTyping, typedText, fullText]);

  useEffect(() => {
    if (isTyping && typedText.length === fullText.length) {
      setShowStartButton(true);
    } else if (typedText.length < fullText.length) {
      setShowStartButton(false);
    }
  }, [isTyping, typedText, fullText]);

  const handleStartClick = () => {
    if (onAnimationComplete) {
      onAnimationComplete();
    }
  };

  const accentColor = isLatinAd ? '#60A5FA' : Colors.primary;
  const textColor = isLatinAd ? '#60A5FA' : Colors.primary;
  const secondaryTextColor = isLatinAd ? '#94A3B8' : Colors.coal;
  const bgStyle = isLatinAd
    ? 'linear-gradient(to bottom right, #0F172A, #1E3A8A, #0F172A)'
    : Colors.background;
  const buttonBg = isLatinAd
    ? 'linear-gradient(to left, #218AE7, #29509F)'
    : domain === 'movImagen' ? 'white' : Colors.blueDark;
  const buttonColor = domain === 'movImagen' ? Colors.primary : Colors.snow;
  const buttonBorderColor = domain === 'movImagen' ? Colors.primary : Colors.blueDark;

  return (
    <div className="ai-loader-container">
      <div className="loader-content">
        <div className="logo-container">
          <div className="particles-background">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="star-particle"
                style={{
                  left: `${50 + 40 * Math.cos((i * 30 * Math.PI) / 180)}%`,
                  top: `${50 + 40 * Math.sin((i * 30 * Math.PI) / 180)}%`,
                  animationDelay: `${i * 0.1}s`
                }}
              />
            ))}
          </div>

          <div className="logo-svg-wrapper">
            {isLatinAd ? (
              <svg
                viewBox="0 0 1139 1139"
                className="w-full h-full"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#60A5FA" />
                    <stop offset="50%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#1D4ED8" />
                  </linearGradient>
                </defs>
                <rect width="1139" height="1139" fill="transparent" />
                <g>
                  <rect
                    x="503.32"
                    y="503.32"
                    width="132.36"
                    height="132.36"
                    fill="url(#logoGradient)"
                    style={{
                      opacity: isTyping ? 1 : progress > 10 ? 1 : 0,
                      transition: 'opacity 0.5s ease-in-out'
                    }}
                  />
                  <rect
                    x="701.86"
                    y="701.86"
                    width="66.18"
                    height="66.18"
                    fill="url(#logoGradient)"
                    style={{
                      opacity: isTyping ? 1 : progress > 30 ? 1 : 0,
                      transition: 'opacity 0.5s ease-in-out'
                    }}
                  />
                  <polygon
                    points="437.14,503.32 370.97,503.32 370.97,768.03 635.68,768.03 635.68,701.86 437.14,701.86"
                    fill="url(#logoGradient)"
                    style={{
                      opacity: isTyping ? 1 : progress > 50 ? 1 : 0,
                      transition: 'opacity 0.5s ease-in-out'
                    }}
                  />
                  <rect
                    x="370.97"
                    y="370.97"
                    width="66.18"
                    height="66.18"
                    fill="url(#logoGradient)"
                    style={{
                      opacity: isTyping ? 1 : progress > 70 ? 1 : 0,
                      transition: 'opacity 0.5s ease-in-out'
                    }}
                  />
                  <polygon
                    points="503.32,370.97 503.32,437.14 701.86,437.14 701.86,635.68 768.03,635.68 768.03,370.97"
                    fill="url(#logoGradient)"
                    style={{
                      opacity: isTyping ? 1 : progress > 90 ? 1 : 0,
                      transition: 'opacity 0.5s ease-in-out'
                    }}
                  />
                </g>
              </svg>
            ) : (
              <img
                src={whiteLabelLogo}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            )}
          </div>

          <div className="rotating-ring" />
          <div className="pulsing-ring" />
        </div>

        <div className="loader-actions">
          {!isTyping ? (
            <>
              <Text style={{ color: textColor, fontSize: '14px' }}>Procesando con IA...</Text>
              <Progress
                percent={progress}
                showInfo={false}
                strokeColor={isLatinAd ? { from: '#3B82F6', to: '#22D3EE' } : Colors.primary}
                style={{ width: '128px' }}
              />
              <Text style={{ color: secondaryTextColor, fontSize: '12px' }}>{progress}%</Text>
            </>
          ) : (
            <div style={{ maxWidth: '672px', width: '100%', padding: '0 1rem' }}>
              {fullText.length > 0 && (
                <Paragraph
                  style={{
                    color: textColor,
                    fontSize: '14px',
                    lineHeight: '1.6',
                    textAlign: 'center'
                  }}
                >
                  {typedText}
                  <span style={{ animation: 'pulse 1.2s infinite ease-in-out' }}>|</span>
                </Paragraph>
              )}
              {showStartButton && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleStartClick}
                    className="searchAIPlannerButton"
                  >
                    {t('StartSearch')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          {floatingParticles.map((particle, i) => (
            <div
              key={`particle-${i}`}
              className="float-particle"
              style={{
                left: particle.left,
                top: particle.top,
                animationDelay: particle.animationDelay
              }}
            />
          ))}
        </div>
      </div>
      <style>
        {`
            @keyframes orbit {
                from {
                    transform: rotate(0deg) translateX(140px) rotate(0deg);
                }
                to {
                    transform: rotate(360deg) translateX(140px) rotate(-360deg);
                }
            }

            @keyframes float {
                0%, 100% {
                    transform: translateY(0px) scale(1);
                    opacity: 0.4;
                }
                50% {
                    transform: translateY(-20px) scale(1.2);
                    opacity: 0.8;
                }
            }

            .ai-loader-container {
                display: flex;
                padding: 50px 0;
                align-items: center;
                justify-content: center;
                min-height: 100%;
                background: ${bgStyle};
                position: relative;
                overflow: hidden;
                border-radius: 16px;
            }

            .loader-content {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                position: relative;
            }
            .logo-container {
                position: relative;
                z-index: 10;
                width: 8rem;
                height: 8rem;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .logo-svg-wrapper {
                position: relative;
                width: 6rem;
                height: 6rem;
            }

            .rotating-ring {
                position: absolute;
                inset: 0;
                border-radius: 9999px;
                border-width: 2px;
                border-color: transparent;
                border-top-color: ${accentColor};
                border-right-color: ${accentColor};
                animation: spin 3s linear infinite;
            }

            .pulsing-ring {
                position: absolute;
                inset: 0;
                border-radius: 9999px;
                border: 1px solid ${accentColor}4D;
                animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
            }

            .particles-background {
                position: absolute;
                width: 24rem;
                height: 24rem;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);
                pointer-events: none;
            }

            .star-particle, .float-particle {
                position: absolute;
                border-radius: 9999px;
            }

            .star-particle {
                width: 4px;
                height: 4px;
                background-color: ${accentColor};
                animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
            }

            .float-particle {
                width: 4px;
                height: 4px;
                background-color: ${accentColor};
                opacity: 0.4;
                animation: float 3s ease-in-out infinite;
            }

            .loader-actions {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin-top: 2rem;
            }

            .loader-actions > *:not(:last-child) {
                margin-bottom: 16px;
            }
            .searchAIPlannerButton {
                top: 7px;
                margin: 0 0 15px 10px;
                height: 34px;
                font-size: 16px;
                border-radius: 12px;
                padding: 0 12px !important;
                color: ${buttonColor};
                background: ${buttonBg};
                border-color: ${buttonBorderColor};
                transition: background 0s ease, border-color 0s ease;
                @media (max-width: 480px) {
                    margin: 0 0 15px 10px;
                }
            }
            .searchAIPlannerButton:hover {
                color: ${domain === 'movImagen' ? Colors.primary : Colors.white};
                background-color: ${domain === 'movImagen' ? 'white' : Colors.primary} !important;
                border-color: ${Colors.primary};
            }

            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }

            @keyframes ping {
                75%, 100% {
                    transform: scale(1.5);
                    opacity: 0;
                }
            }

            @keyframes pulse {
                50% {
                    opacity: .5;
                }
            }

            @keyframes orbit {
                from {
                    transform: rotate(0deg) translateX(140px) rotate(0deg);
                }
                to {
                    transform: rotate(360deg) translateX(140px) rotate(-360deg);
                }
                }

                @keyframes float {
                0%, 100% {
                    transform: translateY(0px) scale(1);
                    opacity: 0.4;
                }
                50% {
                    transform: translateY(-20px) scale(1.2);
                    opacity: 0.8;
                }
            }
            `}
      </style>
    </div>
  );
}
